// Ponte fra l'app Capacitor e il login OAuth.
//
// Perché serve un ponte: Google **rifiuta le WebView incorporate**
// (`disallowed_useragent`), quindi il consenso non si può aprire dentro
// l'app. Si apre nel browser di sistema (`@capacitor/browser`), che però è un
// altro contenitore: la pagina di rientro scambia il codice lì e la sessione
// che ne esce l'app non la vede. Torna indietro con un deep link
// `hostaly://auth/google?handoff=<codice monouso>`, e il codice si scambia con
// `POST /auth/native/session` **dentro l'app** — il gettone non passa mai da
// un URL, che su Android può essere letto da un'altra app registrata sullo
// stesso schema.
//
// I plugin vivono solo in `mobile/package.json`: l'`import` è dinamico e
// marcato `webpackIgnore` perché la build web (CRA) non deve provare a
// risolverli — morirebbe con "Module not found" per un ramo che sul web non
// gira mai. Su web queste funzioni non caricano niente.

import { isNativeApp } from "./platform";

//: Lo schema registrato nel guscio (iOS `CFBundleURLTypes`, Android
//: `intent-filter`). Deve restare uguale in `mobile/README.md`.
export const APP_SCHEME = "hostaly";
export const NATIVE_AUTH_URL = `${APP_SCHEME}://auth/google`;

/** Il deep link con cui la pagina di rientro passa il codice all'app. */
export const buildNativeAuthLink = (handoff) =>
  `${NATIVE_AUTH_URL}?handoff=${encodeURIComponent(String(handoff || ""))}`;

/**
 * Legge `hostaly://auth/google?handoff=…` e restituisce `{ handoff }`, oppure
 * `null` se l'URL non è un rientro di login (un altro deep link dell'app, un
 * link qualsiasi aperto dal sistema).
 *
 * Non lancia mai: `appUrlOpen` consegna anche URL che non c'entrano, e
 * un'eccezione qui vorrebbe dire un login che si blocca per un link estraneo.
 */
export const parseNativeAuthLink = (url) => {
  if (!url) return null;
  let parsed;
  try {
    parsed = new URL(String(url));
  } catch {
    return null;
  }
  if (parsed.protocol !== `${APP_SCHEME}:`) return null;
  if ((parsed.host || "").toLowerCase() !== "auth") return null;
  if (parsed.pathname.replace(/^\/+|\/+$/g, "") !== "google") return null;
  const handoff = parsed.searchParams.get("handoff") || "";
  return handoff ? { handoff } : null;
};

// I plugin si cercano **prima** nel bridge nativo (`Capacitor.Plugins`, che il
// WebView popola per ogni plugin presente nel guscio) e solo dopo nel modulo:
// il bundle CRA non include `@capacitor/*` (vive in `mobile/package.json`),
// quindi un `import` che nessuno ha impacchettato è una promessa, non un fatto.
const loadBrowser = async () => {
  const bridge = typeof window !== "undefined" ? window.Capacitor?.Plugins?.Browser : null;
  if (bridge) return { Browser: bridge };
  return import(/* webpackIgnore: true */ "@capacitor/browser");
};

const loadApp = async () => {
  const bridge = typeof window !== "undefined" ? window.Capacitor?.Plugins?.App : null;
  if (bridge) return { App: bridge };
  return import(/* webpackIgnore: true */ "@capacitor/app");
};

/**
 * Apre l'URL di consenso. Nel guscio usa il browser di sistema; sul web naviga
 * la pagina come ha sempre fatto (un solo punto da chiamare per entrambi).
 */
export async function openAuthWindow(url) {
  if (!url) return;
  if (!isNativeApp()) {
    window.location.href = url;
    return;
  }
  const { Browser } = await loadBrowser();
  // `popup` è la presentazione che iOS accetta per OAuth: un foglio sul
  // contesto dell'app, non una scheda che sostituisce il sito.
  await Browser.open({ url, presentationStyle: "popup" });
}

/** Chiude il browser di sistema dopo il rientro (nel guscio). */
export async function closeAuthWindow() {
  if (!isNativeApp()) return;
  try {
    const { Browser } = await loadBrowser();
    await Browser.close();
  } catch { /* già chiuso, o plugin assente: niente da fare */ }
}

//: Un codice di rientro si scambia **una volta sola**, e questo registro è la
//: difesa prima della rete: lo stesso link può arrivare due volte — l'evento
//: `appUrlOpen` e, se l'app era chiusa, anche `getLaunchUrl()` — e il secondo
//: scambio risponderebbe 401 su un login già riuscito, con un errore mostrato
//: a chi è appena entrato. Il backend lo rifiuta comunque (monouso); qui si
//: evita di chiederglielo.
const handoffsVisti = new Set();

/**
 * In ascolto sui deep link del guscio. Restituisce la funzione che stacca
 * l'ascoltatore (da chiamare nell'unmount): senza, un rimontaggio della pagina
 * di login lascerebbe un secondo ascoltatore che scambia lo stesso codice.
 *
 * Copre anche l'avvio **da** deep link: il sistema può aver chiuso il WebView
 * mentre l'utente era su Google, e in quel caso iOS non emette `appUrlOpen` —
 * l'URL con cui l'app è partita si legge con `getLaunchUrl()`. Senza questa
 * gamba il login si perdeva proprio nel caso più comune in un telefono con
 * poca memoria: si torna dall'app e si resta fuori.
 */
export async function onAuthDeepLink(handler) {
  if (!isNativeApp()) return () => {};
  const { App } = await loadApp();
  const dispatch = (url) => {
    const link = parseNativeAuthLink(url);
    if (!link || handoffsVisti.has(link.handoff)) return;
    handoffsVisti.add(link.handoff);
    handler(link);
  };
  const listener = await App.addListener("appUrlOpen", (event) => dispatch(event?.url));
  // `?.` perché `getLaunchUrl` non c'è nelle versioni vecchie del plugin: un
  // login che funziona non deve dipendere dall'aggiornamento del guscio.
  try {
    const lancio = await App.getLaunchUrl?.();
    if (lancio?.url) dispatch(lancio.url);
  } catch { /* nessun URL di lancio, o plugin che non lo espone */ }
  return () => {
    try { listener?.remove?.(); } catch { /* già rimosso */ }
  };
}
